import { enforce, omitWhen, test } from 'vest';

import {
  isTimeZone,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface HotelkitService {
  locale: 'en' | 'de';
  timezone: string;
  customerKey: string;
  publicKey: string;
  privateKey: string;
  enabledGuestRequestTypes: string[];
}

export const HOTELKIT_REQUEST_TYPES = [
  'amenities',
  'bar',
  'bar2',
  'bar3',
  'bar4',
  'beauty',
  'booking',
  'childCare',
  'childItems',
  'childServices',
  'coffeeTeaItems',
  'complaint',
  'concierge',
  'events',
  'extendStay',
  'facility',
  'feedback',
  'fiaker',
  'frontOffice',
  'greenOption',
  'housekeeping',
  'iceBuckets',
  'lateCheckOut',
  'laundry',
  'luggage',
  'massage',
  'message',
  'minibar',
  'nextStay',
  'petItems',
  'petServices',
  'pillowsBlankets',
  'repair',
  'restaurant',
  'restaurant2',
  'restaurant3',
  'restaurant4',
  'roomService',
  'roomServicePickup',
  'shoeShineServices',
  'shop',
  'shop2',
  'shop3',
  'shuttleServices',
  'spa',
  'spaServices',
  'spaShop',
  'specialServices',
  'specialServices2',
  'specialServices3',
  'sweets',
  'taxi',
  'technologyItems',
  'transfer',
  'turndown',
  'wakeUpCall',
];

export const HOTELKIT_LOCALES = ['en', 'de'];

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    locale: 'en',
    timezone: '',
    customerKey: '',
    publicKey: '',
    privateKey: '',
    enabledGuestRequestTypes: [],
  },
  features: {},
  cron: [],
  webhooks: [{ name: 'processor', enabled: true }],
});

export const hotelkit: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as HotelkitService;

    test(`${SERVICE}.locale`, t('validations.invalid'), () => {
      enforce(service.locale).inside(HOTELKIT_LOCALES);
    });

    test(`${SERVICE}.customerKey`, t('validations.required'), () => {
      enforce(service.customerKey).isNotBlank();
    });

    test(`${SERVICE}.timezone`, t('validations.required'), () => {
      enforce(service.timezone).isNotBlank();
    });

    omitWhen(!service.timezone, () => {
      test(`${SERVICE}.timezone`, t('validations.invalid'), () => {
        enforce(isTimeZone(service.timezone)).isTruthy();
      });
    });
  },
};
